import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next"; // Import useTranslation
import type {
  TransformedProduct,
  TransformedVariation,
  EnrichedAttributeTransformed,
} from "../../types/productApiTypes";

// --- Props ---
interface ProductPanelsProps {
  // Accept the full product object
  product: TransformedProduct;
  // Still need loading state for the product itself
  isLoading: boolean;
  // Still need selected variation for potential description override
  selectedVariation?: TransformedVariation | null;
}

// --- Component ---
const ProductPanels: React.FC<ProductPanelsProps> = ({
  product,
  isLoading, // This prop now includes translation readiness from parent
  selectedVariation = null,
}) => {
  const { t } = useTranslation(); // Initialize useTranslation

  // --- Extract data from product prop ---
  const description = product.description;
  const enrichedAttributes = product.enrichedAttributes || []; // Use enrichedAttributes
  const averageRating = product.averageRating;
  // Determine which description to show (variation overrides parent)
  const displayDescription = selectedVariation?.description || description;
  const reviewCount = product.reviewCount || 0; // Get review count

  // --- Check for content ---
  const hasDescription = displayDescription && displayDescription.trim() !== "";
  // Check based on enrichedAttributes
  const hasAttributes = enrichedAttributes.length > 0;
  // Reviews panel is always shown, even if empty for now
  const hasReviews = true;

  // Initialize with no active panel (all closed)
  const [activePanel, setActivePanel] = useState<string | null>(null);

  const handlePanelClick = (panelId: string): void => {
    // If the clicked panel is already active, close it (set to null)
    // Otherwise, open the clicked panel
    setActivePanel(activePanel === panelId ? null : panelId);
  };

  // --- Skeleton ---
  if (isLoading) {
    return <ProductPanelsSkeleton />;
  }

  // --- Render Sections ---
  return (
    <div className="product-sections">
      {/* Description Section */}
      {hasDescription && (
        <div className="product-section">
          <button
            type="button"
            className={`product-section-header ${
              activePanel === "description" ? "active" : ""
            }`}
            onClick={() => handlePanelClick("description")}
            aria-expanded={activePanel === "description"}
            aria-controls="description-panel"
            id="description-header"
          >
            {t("product.description")}
            <span className="toggle-icon" aria-hidden="true"></span>
          </button>
          <div
            id="description-panel"
            className={`panel-content ${
              // Renamed class
              activePanel === "description" ? "active" : ""
            }`}
            role="region" // Changed from tabpanel to region
            aria-labelledby="description-header"
          >
            {/* Added inner div for padding consistency */}
            <div className="panel-content__inner">
              {" "}
              {/* Renamed class */}
              <div dangerouslySetInnerHTML={{ __html: displayDescription }} />
            </div>
          </div>
        </div>
      )}

      {/* Properties Section */}
      {hasAttributes && (
        <div className="product-section">
          <button
            type="button"
            className={`product-section-header ${
              activePanel === "properties" ? "active" : ""
            }`}
            onClick={() => handlePanelClick("properties")}
            aria-expanded={activePanel === "properties"}
            aria-controls="properties-panel"
            id="properties-header"
          >
            {t("product.properties")}
            <span className="toggle-icon" aria-hidden="true"></span>
          </button>
          <div // Renamed class
            id="properties-panel" // ID remains the same for aria control
            className={`panel-content ${
              activePanel === "properties" ? "active" : ""
            }`}
            role="region" // Changed from tabpanel to region
            aria-labelledby="properties-header"
          >
            {/* Added inner div for padding consistency */}
            <div className="panel-content__inner">
              {" "}
              {/* Renamed class */}
              <table className="product-attributes">
                <tbody>
                  {/* Iterate over the enriched attributes array */}
                  {enrichedAttributes.map(
                    (attribute: EnrichedAttributeTransformed) => {
                      // Extract term names from the attribute's terms array
                      const termNames = attribute.terms.map(
                        (term) => term.name
                      );

                      // Don't render row if attribute has no terms to display
                      if (termNames.length === 0) {
                        return null;
                      }

                      return (
                        <tr key={attribute.id}>
                          {/* Use the final attribute name */}
                          <th>{attribute.name}</th>
                          {/* Join the final term names */}
                          <td>{termNames.join(", ")}</td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Reviews Section */}
      {hasReviews && (
        <div className="product-section">
          <button
            type="button"
            className={`product-section-header ${
              activePanel === "reviews" ? "active" : ""
            }`}
            onClick={() => handlePanelClick("reviews")}
            aria-expanded={activePanel === "reviews"}
            aria-controls="reviews-panel"
            id="reviews-header"
          >
            {t("product.reviews")} ({reviewCount})
            <span className="toggle-icon" aria-hidden="true"></span>
          </button>
          <div // Renamed class
            id="reviews-panel" // ID remains the same for aria control
            className={`panel-content ${
              activePanel === "reviews" ? "active" : ""
            }`}
            role="region" // Changed from tabpanel to region
            aria-labelledby="reviews-header"
          >
            {/* Added inner div for padding consistency */}
            <div className="panel-content__inner">
              <div className="product-reviews">
                {averageRating && parseFloat(averageRating) > 0 && (
                  <div className="average-rating">
                    <div
                      className="rating-stars"
                      aria-label={`Rated ${averageRating} out of 5 stars`}
                    >
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`star ${
                            parseFloat(averageRating) >= star ? "filled" : ""
                          }`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                    <div className="rating-text">
                      {averageRating} out of 5 stars
                    </div>
                  </div>
                )}

                {/* Review form placeholder */}
                <div className="review-form-placeholder">
                  <p>{t("product.reviewsComingSoon")}</p> {/* Translate */}
                  <button className="button" disabled aria-disabled="true">
                    {t("product.writeReview")} {/* Translate */}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Skeleton Component ---
interface ProductPanelsSkeletonProps {}
const ProductPanelsSkeleton: React.FC<ProductPanelsSkeletonProps> = () => {
  // Assume 3 sections for skeleton: Description, Properties, Reviews
  const sectionCount = 3;
  return (
    <div className="product-sections-loading">
      {[...Array(sectionCount)].map((_, index) => (
        <div className="product-section-skeleton" key={index}>
          <Skeleton height={45} className="section-header-skeleton" />
          {/* REMOVED: No skeleton content shown by default */}
        </div>
      ))}
    </div>
  );
};

// --- Export with Skeleton ---
type ProductPanelsComponent = React.FC<ProductPanelsProps> & {
  Skeleton: React.FC<ProductPanelsSkeletonProps>;
};
(ProductPanels as ProductPanelsComponent).Skeleton = ProductPanelsSkeleton;
export default ProductPanels as ProductPanelsComponent;
